import React from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
} from '@mui/material';
import { UserRole } from 'aws-sdk/clients/workmail';
import { useChangeUserRoleMutation } from 'src/common/apis/AdminApi';
import { User, UserRoles } from 'src/common/types/User';
import SnackbarUtils from 'src/common/utils/SnackbarUtilsConfigurator';

import { ReactComponent as CancelIconBlue } from '../../../../common/assets/icons/cancel_icon_blue.svg';

type Props = {
  user: User;
  onClickClose: () => void;
};

const getDefaultRole = (accessRoles: string[]) => {
  if (accessRoles.includes(UserRoles.ORG_OWNER)) {
    return UserRoles.ORG_OWNER;
  }

  if (accessRoles.includes(UserRoles.CODER)) {
    return UserRoles.CODER;
  }

  if (accessRoles.includes(UserRoles.USER)) {
    return UserRoles.USER;
  }

  return '';
};

const ChangeUserRolePopup: React.FC<Props> = ({ onClickClose, user }) => {
  const [value, setValue] = React.useState<UserRoles | string>(
    getDefaultRole(user.accessRoles)
  );

  const [changeRole, { isLoading }] = useChangeUserRoleMutation();
  const submit = () => {
    changeRole({
      userId: user.id,
      newRole: value as UserRoles,
    })
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);
        onClickClose();
      });
  };

  if (!user) return <></>;

  return (
    <Box
      sx={{
        maxWidth: '456px',
        minHeight: '240px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flex: 1,
        borderRadius: '15px',
        padding: '40px',
        flexDirection: 'column',
      }}
    >
      <Box className='row' sx={{ alignItems: 'center' }}>
        <IconButton
          onClick={onClickClose}
          sx={{ alignSelf: 'flex-start', justifySelf: 'right' }}
        >
          <CancelIconBlue />
        </IconButton>
        <Typography
          sx={{
            alignSelf: 'center',
            whiteSpace: 'break-spaces',
            textAlign: 'center',
            mb: '24px',
            ml: 'auto',
            mr: 'auto',
          }}
          color='text.primary'
          variant='h5'
        >
          Change User Role
        </Typography>
        <Box sx={{ mr: 'auto' }} />
      </Box>
      <Box>
        <Select
          value={value}
          onChange={(e) => setValue(e.target.value as UserRole)}
          fullWidth
        >
          <MenuItem value={UserRoles.ORG_OWNER} disabled={true}>
            Owner
          </MenuItem>
          <MenuItem value={UserRoles.CODER}>Coder</MenuItem>
          <MenuItem value={UserRoles.USER}>Member</MenuItem>
        </Select>
      </Box>
      <Box
        sx={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
          display: 'flex',
          marginTop: '20px',
        }}
      >
        <Button
          onClick={onClickClose}
          disableElevation
          color='appGray2'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
            marginRight: '15px',
          }}
        >
          <Typography color='white'>Cancel</Typography>
        </Button>
        <LoadingButton
          onClick={submit}
          disabled={isLoading}
          loading={isLoading}
          disableElevation
          color='primary'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
          }}
        >
          <Typography color='white'>Ok</Typography>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ChangeUserRolePopup;
